import { Dayjs } from "dayjs";
import { api } from "../../../config/api";
import { guarded, pathSegment } from "../../../utils/url";
import { OrderFormType } from "./AddOrder";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { OrderRowType } from "./OrderData";

export const fetchAllOrder = (
  startDate: Dayjs,
  endDate: Dayjs,
  page: number,
  shipment: string,
  status?: string
) => {
  const fromDate = startDate.format("YYYY-MM-DD");
  const toDate = endDate.format("YYYY-MM-DD");

  return api.get(`/b2c/orders/`, {
    params: {
      from_date: fromDate,
      to_date: toDate,
      // page: page,
      // page_size: 2000,
      shipment: shipment,
      status: status || "",
    },
  });
};

export const bulkOrderUpload = (file: File | null) => {
  return api.post(
    "/b2c/csv_order_create/",
    { file: file },
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

export const storeOrder = (data: OrderFormType) => {
  return api.post("/b2c/create_order/", data);
};

export const fetchRateRequest = (id: any) => {
  return api.get("/b2c/request_rate/", {
    params: {
      id: id,
    },
  });
};

export const assignOrder = ({
  id,
  quote_id,
}: {
  id: number | string;
  quote_id: number | string;
}) => {
  return api.post("/b2c/assign_shipment/", {
    id,
    quote_id,
  });
};

export const bookOrderMethod = (arrayOfOrderId: string[]) => {
  return api.post("/b2c/book_shipment/", arrayOfOrderId);
};
export const addOrderTagMethod = ({  
  id,
  tag,
}: {
  id: number | string;
  tag: string;
}) => {
  return api.post("/b2c/order_tag/", {
    id,
    tag,
  });
};

interface scheduleDate {
  id: string | number;
  pickup_date: string;
}

export const scheduleOrder = (data: scheduleDate[]) => {
  return api.post("/b2c/schedule_pickup/", data);
};

export const getOrderDetails = (id: string) => {
  return api.get("b2c/shipment_details/", {
    params: {
      id,
    },
  });
};

export const cancelShipment = (data: string[]) => {
  return api.post("/b2c/cancel_shipment/", data);
};
// Single order cancellation. For many orders use cancelBulkOrders (/b2c/cancel_bulk_order/).
export const cancelOrder = (data: string[]) => {
  return api.post("/b2c/cancel_order/", data);
};

export const downloadAwb = (awbNo: any) => {
  return api.get("/b2c/download_awb/", 
    {
    params: {
      awbNo,
    },
    responseType: "blob",
  });
};
export const downloadPod = (lr: any) => {
  return api.get("/b2c/download_pod/", 
    {
    params: {
      lr,
    },
    responseType: "blob",
  });
};
export const bulkdownloadAwb = (data: any) => {
  return api.post(
    "/b2c/bulk_print/",
    {
      doc_type: "label",
      shipment_ids: data.shipment_ids || [],
    },
    {
      responseType: "blob",
    }
  );
};

// export const bulkdownloadPod = (data: any) => {
//   console.log("data",data);
//   return api.post(
//     "/b2c/bulk_download/",
//     {
//       doc_type: "label",
//       shipment_ids: data.shipment_ids || [],
//     },
//     {
//       responseType: "blob",
//     }
//   );
// };
export const bulkdownloadPod = (opts: { lrs?: string[] | string, doc_type?: string }) => {
  // ensure irs is a comma separated string
  const lrs = Array.isArray(opts.lrs) ? opts.lrs.join(",") : opts.lrs || "";

  // Use GET with query param ?irs=1,2,3 — matches https://hipship.com/b2c/bulk_download/?irs=...
  return api.get("/b2c/bulk_download/", {
    params: { lrs},
    responseType: "blob",
  });

  // If backend requires POST (but still reads query params), swap to:
  // return api.post("/b2c/bulk_download/", { doc_type: opts.doc_type || "label" }, { params: { irs }, responseType: "blob" });
};

export const downloadProForma = (id: any) => {
  return api.get("b2c/pro_forma_invoice/", {
    params: {
      id,
    },
    responseType: "blob",
  });
};

export const assignMultipleService = ({
  id,
  service,
  mode,
}: {
  id: string;
  service: string;
  mode: string;
}) => {
  return api.post("b2c/csv/vendor_service/", {
    id,
    service,
    mode,
  });
};

export const trackOrder = (id: string) => {
  return api.get("/b2c/track_shipment/", {
    params: {
      id,
    },
  });
};
export const trackOrderAwb = (awbNo: string) => {
  return api.get(`/b2c/track_shipment/?awbNo=${awbNo}`, {
    
  });
};

export const trackOrderAwbNo = (awbNo: string) => {
  return api.get(`/b2c/shipment_tracking/`, {
    params: {
      awbNo: awbNo,
    },
  });
};

export const trackOrderLr = (lrNo: string) => {
  return api.get(`/b2c/shipment_tracking_lrnum/`, {
    params: {
      lrnum: lrNo,
    },
  });
};

// -------------------------------------------------------------------------
// Buyer ticket (tracking-page chatbot) APIs — public, no auth required.
// -------------------------------------------------------------------------
export interface BuyerTicketPayload {
  awb_no: string;
  category: string;
  sub_category?: string;
  query_remark?: string;
  buyer_name?: string;
  buyer_phone?: string;
  buyer_email?: string;
  // the chatbot conversation so far, saved on the ticket for the team
  transcript?: ChatLine[];
  // true: the chatbot answered it - save as a closed, auto-replied ticket
  self_service?: boolean;
}

export interface ChatLine {
  from: "bot" | "user";
  text: string;
}

export interface BuyerTicketResponse {
  ticket_id: string;
  status: string;
  created_at: string;
  // instant answer from the HipShip ticket assistant (also posted in the ticket chat)
  assistant_reply?: string | null;
}

export const raiseBuyerTicket = (data: BuyerTicketPayload) => {
  return api.post("/b2c/buyer_ticket/", data);
};

// What the ticket assistant would answer for this AWB + issue, BEFORE a ticket
// is raised. self_service = the answer covers the question (delivered, on
// schedule, out for delivery, being returned), so the buyer may not need a ticket.
export const precheckBuyerTicket = (params: { awb_no: string; category: string; sub_category?: string }) => {
  return api.get("/b2c/buyer_ticket_precheck/", { params });
};

// Adds the rest of a chatbot conversation to its auto-replied ticket;
// escalate=true reopens that same ticket for the support team.
export const saveBuyerTicketTranscript = (ticket_id: string, data: { lines: ChatLine[]; escalate: boolean }) => {
  return api.post(`/b2c/buyer_ticket/${encodeURIComponent(ticket_id)}/transcript/`, data);
};

export const getBuyerTicketStatus = (params: { ticket_id?: string; awb_no?: string }) => {
  return api.get("/b2c/buyer_ticket_status/", {
    params,
  });
};

export interface TicketMessageType {
  id: number;
  sender_type: "admin" | "client" | "buyer";
  sender_name: string;
  message: string;
  created_at: string;
  is_edited?: boolean;
  edited_at?: string;
  attachment_url?: string;
  attachment_name?: string;
  attachment_mime_type?: string;
  // true when the reply was written by Hipship AI (the ticket autopilot)
  is_ai?: boolean;
  // true when THIS buyer sent this particular message - only then can they
  // edit/unsend it (there's exactly one buyer per ticket, so this is just
  // "did I send this")
  can_edit?: boolean;
}

// Full chat thread for a ticket - accessible with just the ticket_id, same
// trust model as the status lookup above (buyers are anonymous, so knowing
// the ticket_id IS the access control here, there's no login to check).
export const getBuyerTicketMessages = (ticket_id: string) => {
  return guarded(() => api.get(`/b2c/buyer_ticket/${pathSegment(ticket_id, "ticket id")}/messages/`));
};

// `file` is uploaded straight through to the ticket's Google Drive folder on
// the backend - never stored in our own DB/bucket.
export const sendBuyerTicketMessage = (ticket_id: string, message: string, file?: File) =>
  guarded(() => {
    const url = `/b2c/buyer_ticket/${pathSegment(ticket_id, "ticket id")}/messages/`;
    if (file) {
      const formData = new FormData();
      formData.append("message", message);
      formData.append("attachment", file);
      return api.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
    return api.post(url, { message });
  });

export const updateBuyerTicketMessage = (ticket_id: string, message_id: number, message: string) => {
  return guarded(() =>
    api.patch(`/b2c/buyer_ticket/${pathSegment(ticket_id, "ticket id")}/messages/${pathSegment(message_id, "message id")}/`, { message })
  );
};

export const deleteBuyerTicketMessage = (ticket_id: string, message_id: number) => {
  return guarded(() =>
    api.delete(`/b2c/buyer_ticket/${pathSegment(ticket_id, "ticket id")}/messages/${pathSegment(message_id, "message id")}/`)
  );
};
// Orders for one tab. Cached per (dates, shipment, status) so switching back to a tab you've
// already opened is instant, while a background refetch keeps it fresh.
export function useOrders({
  startDate,
  endDate,
  shipment,
  status,
  enabled = true,
}: {
  startDate: Dayjs;
  endDate: Dayjs;
  shipment: string;
  status: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      "orders",
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
      shipment,
      status,
    ],
    queryFn: async () => {
      const res = await fetchAllOrder(startDate, endDate, 1, shipment, status);
      return (res?.data?.data?.data ?? []) as OrderRowType[];
    },
    enabled,
    staleTime: 30 * 1000,
    retry: 1,
  });
}

// One page of a paged /b2c/orders/ call (`data.data` of the response).
export interface OrdersPage {
  data: OrderRowType[];
  count: number;
  page: number;
  page_size: number;
  num_pages: number;
  not_found?: string[];
}

// Paging is opt-in on the server: `page_size` (with a 1-based `page`) returns one page plus
// the total; `search` and `vendor` are filtered on the server too.
export const fetchOrdersPage = (params: {
  startDate: Dayjs;
  endDate: Dayjs;
  shipment: string;
  status?: string;
  page: number;
  pageSize: number;
  search?: string;
  vendor?: string;
}) => {
  const query: Record<string, string | number> = {
    from_date: params.startDate.format("YYYY-MM-DD"),
    to_date: params.endDate.format("YYYY-MM-DD"),
    shipment: params.shipment,
    status: params.status || "",
    page: params.page,
    page_size: params.pageSize,
  };
  if (params.search?.trim()) query.search = params.search.trim();
  if (params.vendor && params.vendor !== "All") query.vendor = params.vendor;
  return api.get(`/b2c/orders/`, { params: query });
};

// Orders for one tab, one server page at a time. The previous page stays on screen while the
// next one loads; each (filters, page) is cached briefly so going back is instant.
export function useOrdersPage({
  startDate,
  endDate,
  shipment,
  status,
  page,
  pageSize,
  search,
  vendor,
  enabled = true,
}: {
  startDate: Dayjs;
  endDate: Dayjs;
  shipment: string;
  status: string;
  page: number; // 0-based (DataGrid)
  pageSize: number;
  search: string;
  vendor: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      "orders",
      "page",
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD"),
      shipment,
      status,
      search,
      vendor,
      page,
      pageSize,
    ],
    queryFn: async (): Promise<OrdersPage> => {
      const res = await fetchOrdersPage({
        startDate,
        endDate,
        shipment,
        status,
        page: page + 1,
        pageSize,
        search,
        vendor,
      });
      const d = res?.data?.data ?? {};
      return {
        data: Array.isArray(d.data) ? d.data : [],
        count: Number(d.count) || 0,
        page: Number(d.page) || page + 1,
        page_size: Number(d.page_size) || pageSize,
        num_pages: Number(d.num_pages) || 0,
        not_found: d.not_found,
      };
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    retry: 1,
  });
}
